import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaChevronDown,
  FaShieldAlt,
  FaSignOutAlt,
  FaTachometerAlt,
  FaUserCircle,
} from "react-icons/fa";
import SuperAdminStorageService from "../../services/SuperAdminStorageService";
import SuperAdminAuthService from "../../services/SuperAdminAuthService";
import "../../assets/styles/common-header-footer.scss";
import usePlatformLogo from "../../hooks/usePlatformLogo";

export default function SuperAdminHeader() {
  const navigate = useNavigate();
  const platformLogoUrl = usePlatformLogo();
  const admin = SuperAdminStorageService.getAdmin();
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        !open ||
        panelRef.current?.contains(e.target) ||
        btnRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      await SuperAdminAuthService.logout();
    } finally {
      SuperAdminStorageService.clearAuth();
      setLoggingOut(false);
      navigate("/super-admin/login", { replace: true });
    }
  }, [navigate]);

  return (
    <header className="common-header">
      <div className="common-header-container">
        <div className="common-header-logo">
          <Link to="/super-admin/dashboard" className="common-header-logo-link">
            <img
              src={platformLogoUrl}
              alt="Trocanet Logo"
              className="common-header-logo-img"
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextSibling) e.target.nextSibling.style.display = "block";
              }}
            />
            <span className="common-header-logo-text">Trocanet</span>
          </Link>
        </div>

        <div className="common-header-nav">
          <Link
            to="/super-admin/dashboard"
            className="common-header-nav-link"
            title="Super Admin Dashboard"
          >
            <FaTachometerAlt className="common-header-nav-icon" />
            <span className="common-header-nav-text">Control Centre</span>
          </Link>
        </div>

        <div className="common-header-nav">
          <span className="common-header-nav-link" style={{ cursor: "default" }}>
            <FaShieldAlt className="common-header-nav-icon" />
            <span className="common-header-nav-text">Super Admin</span>
          </span>
        </div>

        <div className="common-header-profile">
          <button
            ref={btnRef}
            type="button"
            className="common-header-profile-trigger"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-haspopup="true"
          >
            <FaUserCircle className="common-header-profile-icon" />
            <span className="common-header-profile-name">
              {admin?.full_name || admin?.email || "Super Admin"}
            </span>
            <FaChevronDown
              size={12}
              className={`common-header-profile-chevron${open ? " open" : ""}`}
            />
          </button>

          {open ? (
            <div ref={panelRef} className="common-header-profile-panel">
              <div className="common-header-profile-header">
                <strong>{admin?.full_name || "Super Admin"}</strong>
                <span>{admin?.email}</span>
              </div>
              <ul className="common-header-profile-menu">
                <li>
                  <button
                    type="button"
                    className="common-header-profile-item"
                    onClick={handleLogout}
                    disabled={loggingOut}
                  >
                    <FaSignOutAlt aria-hidden />
                    {loggingOut ? "Signing out…" : "Logout"}
                  </button>
                </li>
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
